import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Impact} from '@app/layout/rules/template-editor/api/template';
import {finalize, takeUntil} from 'rxjs/operators';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {AceLogger} from '@logging/ace-logger';
import {Subject, Subscription} from 'rxjs';
import {AceLoggerService} from '@logging/ace-logger.service';
import {TemplatesService} from '@app/layout/rules/template-editor/services/templates.service';
import {I18nMessage} from '@app/shared/api/model/i18n';
import {updateControlEnableState} from '@app/shared/util/angular/angular-utils';

@Component({
  selector: 'app-impacts-select',
  templateUrl: './impacts-select.component.html',
  styleUrls: ['./impacts-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImpactsSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class ImpactsSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor  {
  private readonly logger: AceLogger;
  private readonly ngUnsubscribe = new Subject<void>();

  @Input() appendTo: string;
  @Input() impacts: Impact[];
  @Input() isReadOnly: boolean;
  @Input() loading = false;
  @Input() multiSelect = false;
  @Input() placeholder: string;
  @Input() clearable = true;
  @Input() maxSelectedLabels: number;
  @Input() maxSelectedShowItemName = true;
  @Input() maxSelectedItemName: I18nMessage;
  @Input() truncateLabels = true;
  @Output() impactsLoaded = new EventEmitter<Impact[]>();

  private fetchImpactsSubscription: Subscription;
  formControl = new FormControl<Impact | Impact[]>(null);

  onChange: (_) => void = (_) => {};
  onTouched: () => void = () => {};

  constructor(private templatesService: TemplatesService,
              logService: AceLoggerService) {
    this.logger = logService.create(ImpactsSelectComponent.name);
  }

  ngOnInit() {
    this.formControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => this.onChange(value));
    if (this.impacts == null) {
      this.fetchImpacts();
    }
  }

  ngOnChanges(changes) {
    if (changes.impacts) {
      if (this.impacts) {
        this.loading = false;
        this.cancelSubscription();
      } else {
        this.fetchImpacts();
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.cancelSubscription();
  }

  private fetchImpacts() {
    if (this.fetchImpactsSubscription == null) {
      this.loading = true;
      this.fetchImpactsSubscription = this.templatesService.getImpacts()
        .pipe(finalize(() => {
          this.loading = false;
          this.cancelSubscription();
        })).subscribe((impacts: Impact[]) => {
          this.impacts = impacts;
          this.impactsLoaded.emit(impacts);
        });
    }
  }

  private cancelSubscription() {
    if (this.fetchImpactsSubscription != null) {
      this.fetchImpactsSubscription.unsubscribe();
      this.fetchImpactsSubscription = null;
    }
  }

  writeValue(value: Impact | Impact[]): void {
    if (value && Array.isArray(value) && !this.multiSelect) {
      throw new Error('multiSelect is false so expected single object not array');
    } else if (value && !Array.isArray(value) && this.multiSelect) {
      throw new Error('multiSelect is true so expected array not single object');
    } else {
      this.formControl.setValue(value, {emitEvent: false});
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    updateControlEnableState(this.formControl, !isDisabled);
  }
}
